import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  dashApi, type ManagementAction, type ActionUpdateBody, type ActionEvent, type ActionLink,
} from "@/lib/dashApi";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel, StatusPill, Stat } from "@/components/dash/ui";

const PRIORITY_TONE: Record<string, string> = {
  critical: "text-rose-300 bg-rose-500/10 ring-rose-500/30",
  high: "text-amber-300 bg-amber-500/10 ring-amber-500/30",
  medium: "text-sky-300 bg-sky-500/10 ring-sky-500/30",
  low: "text-slate-300 bg-slate-500/10 ring-slate-500/30",
};
const ACTIVE_STATUSES = ["open", "acknowledged", "in_progress", "blocked", "waiting_on", "needs_decision"];
const ALL_STATUSES = [...ACTIVE_STATUSES, "resolved", "dismissed"];
// Mirrors backend decision_centre.VALID_TRANSITIONS (keep in sync).
const TRANSITIONS: Record<string, string[]> = {
  open: ["acknowledged", "in_progress", "blocked", "waiting_on", "needs_decision", "resolved", "dismissed"],
  acknowledged: ["in_progress", "blocked", "waiting_on", "needs_decision", "resolved", "dismissed", "open"],
  in_progress: ["acknowledged", "blocked", "waiting_on", "needs_decision", "resolved", "dismissed"],
  blocked: ["acknowledged", "in_progress", "waiting_on", "needs_decision", "resolved", "dismissed"],
  waiting_on: ["acknowledged", "in_progress", "blocked", "needs_decision", "resolved", "dismissed"],
  needs_decision: ["acknowledged", "in_progress", "blocked", "waiting_on", "resolved", "dismissed"],
  resolved: ["open"],
  dismissed: ["open"],
};
const CATEGORIES = [
  "finance", "sales", "renewals", "repairs", "support", "catalogue", "staffing",
  "customer", "data_quality", "reconciliation", "platform", "other",
  "revenue", "operations", "stock", "connector",
];

function PriorityPill({ p }: { p: string }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${PRIORITY_TONE[p] ?? PRIORITY_TONE.low}`}>
      {p}
    </span>
  );
}

export default function AccountabilityPage() {
  const overview = useQuery({ queryKey: ["acct-overview"], queryFn: dashApi.accountabilityOverview });
  const metrics = useQuery({ queryKey: ["acct-metrics"], queryFn: () => dashApi.accountabilityMetrics(90) });
  const [filters, setFilters] = useState<{ status?: string; category?: string; priority?: string; owner?: string }>({ status: "active" });
  const actions = useQuery({
    queryKey: ["acct-actions", filters],
    queryFn: () => dashApi.accountabilityActions(filters),
  });

  const c = overview.data?.counts;
  return (
    <div className="space-y-6">
      <PageHeader
        title="Accountability"
        description="Closed-loop management workflow. Every risk, alert, mismatch and finding becomes an ownable, traceable action — who owns it, what's planned, when it's due, and whether it's blocked, overdue or awaiting a decision. IRONDash-only: these actions never write to any source system."
      />

      {overview.isLoading ? <LoadingState /> : overview.isError ? <ErrorState error={overview.error} /> : c && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
          <Stat label="Active" value={c.active} />
          <Stat label="Overdue" value={c.overdue} sub={c.overdue ? "needs attention" : undefined} />
          <Stat label="Due today" value={c.due_today} />
          <Stat label="Due this week" value={c.due_this_week} />
          <Stat label="Unassigned" value={c.unassigned} sub={c.unassigned_high ? `${c.unassigned_high} high+` : undefined} />
          <Stat label="Blocked / waiting" value={c.blocked} />
          <Stat label="Needs decision" value={c.needs_decision} />
          <Stat label="Resolved (7d)" value={c.recently_resolved} />
        </div>
      )}

      {metrics.data && (
        <Panel title="Completion performance (90 days)" subtitle={metrics.data.adequate_data ? undefined : (metrics.data.note ?? undefined)}>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Stat label="Resolved" value={metrics.data.resolved_total} />
            <Stat label="On time" value={metrics.data.resolved_on_time} />
            <Stat label="Late" value={metrics.data.resolved_late} />
            <Stat label="Currently overdue" value={metrics.data.active_overdue} />
            <Stat label="Reopened" value={metrics.data.reopened_total} />
            <Stat label="Avg resolution" value={metrics.data.avg_resolution_days ?? "—"} sub="days" />
          </div>
          <p className="mt-3 text-[11px] text-slate-500">Aggregate only — IRONDash makes no individual-performance claims.</p>
        </Panel>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Panel title="By owner (active)">
          <Breakdown rows={(overview.data?.by_owner ?? []).map((r) => ({ label: r.owner, value: r.count }))} onPick={(owner) => setFilters((f) => ({ ...f, owner: owner === "(unassigned)" ? undefined : owner }))} />
        </Panel>
        <Panel title="By category (active)">
          <Breakdown rows={(overview.data?.by_category ?? []).map((r) => ({ label: r.category, value: r.count }))} onPick={(cat) => setFilters((f) => ({ ...f, category: cat }))} />
        </Panel>
        <Panel title="By priority (active)">
          <Breakdown rows={(overview.data?.by_priority ?? []).map((r) => ({ label: r.priority, value: r.count }))} onPick={(p) => setFilters((f) => ({ ...f, priority: p }))} />
        </Panel>
      </div>

      <Panel title="Actions" subtitle="Filter, then open a row to manage its lifecycle, links and history.">
        <Filters filters={filters} setFilters={setFilters} />
        {actions.isLoading ? <LoadingState /> : actions.isError ? <ErrorState error={actions.error} /> : (
          (actions.data?.actions.length ?? 0) === 0
            ? <EmptyState>No actions match these filters.</EmptyState>
            : <div className="space-y-2">{actions.data!.actions.map((a) => <ActionRow key={a.id} action={a} />)}</div>
        )}
      </Panel>
    </div>
  );
}

function Breakdown({ rows, onPick }: { rows: { label: string; value: number }[]; onPick: (label: string) => void }) {
  if (rows.length === 0) return <EmptyState compact>None.</EmptyState>;
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <ul className="space-y-1.5">
      {rows.map((r) => (
        <li key={r.label}>
          <button onClick={() => onPick(r.label)} className="flex w-full items-center gap-2 text-left text-xs hover:text-sky-200">
            <span className="w-32 shrink-0 truncate text-slate-300">{r.label}</span>
            <span className="h-2 flex-1 rounded bg-slate-800"><span className="block h-2 rounded bg-sky-500/50" style={{ width: `${(r.value / max) * 100}%` }} /></span>
            <span className="w-8 shrink-0 text-right text-slate-400">{r.value}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function Filters({ filters, setFilters }: { filters: Record<string, string | undefined>; setFilters: (f: (p: Record<string, string | undefined>) => Record<string, string | undefined>) => void }) {
  const sel = "rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-200";
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <select className={sel} value={filters.status ?? ""} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value || undefined }))}>
        <option value="">Any status</option>
        <option value="active">Active</option>
        <option value="overdue">Overdue</option>
        <option value="unassigned">Unassigned</option>
        {ALL_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <select className={sel} value={filters.priority ?? ""} onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value || undefined }))}>
        <option value="">Any priority</option>
        {["critical", "high", "medium", "low"].map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
      <select className={sel} value={filters.category ?? ""} onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value || undefined }))}>
        <option value="">Any category</option>
        {CATEGORIES.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
      </select>
      {filters.owner && (
        <button onClick={() => setFilters((f) => ({ ...f, owner: undefined }))} className="rounded bg-slate-800 px-2 py-1 text-xs text-slate-300">owner: {filters.owner} ✕</button>
      )}
      {(filters.category || filters.priority || filters.owner) && (
        <button onClick={() => setFilters(() => ({ status: filters.status }))} className="text-xs text-slate-500 hover:text-slate-300">clear</button>
      )}
    </div>
  );
}

function ActionRow({ action }: { action: ManagementAction }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/30">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full flex-wrap items-center gap-2 px-3 py-2 text-left">
        <StatusPill status={action.status} />
        <PriorityPill p={action.priority} />
        <span className="flex-1 truncate text-sm text-slate-200">{action.title}</span>
        <span className="text-[11px] text-slate-500">{action.category}</span>
        {action.owner ? <span className="text-[11px] text-slate-400">👤 {action.owner}</span> : <span className="text-[11px] text-amber-400">unassigned</span>}
        {action.is_overdue && <span className="text-[11px] text-rose-400">overdue {action.overdue_days}d</span>}
        {action.reopen_count > 0 && <span className="text-[11px] text-violet-300">reopened ×{action.reopen_count}</span>}
        {action.due_date && <span className="text-[11px] text-slate-500">due {action.due_date}</span>}
      </button>
      {open && <ActionDetail action={action} />}
    </div>
  );
}

function ActionDetail({ action }: { action: ManagementAction }) {
  const qc = useQueryClient();
  const detail = useQuery({ queryKey: ["acct-action", action.id], queryFn: () => dashApi.accountabilityAction(action.id) });
  const [status, setStatus] = useState("");
  const [owner, setOwner] = useState(action.owner ?? "");
  const [note, setNote] = useState("");
  const [blocker, setBlocker] = useState(action.blocker_reason ?? "");
  const [decision, setDecision] = useState(action.decision_required ?? "");
  const [evidence, setEvidence] = useState(action.completion_evidence ?? "");
  const [err, setErr] = useState<string | null>(null);
  const invalidate = () => { qc.invalidateQueries({ queryKey: ["acct-overview"] }); qc.invalidateQueries({ queryKey: ["acct-actions"] }); qc.invalidateQueries({ queryKey: ["acct-action", action.id] }); };
  const mut = useMutation({
    mutationFn: (body: ActionUpdateBody) => dashApi.updateAction(action.id, body),
    onSuccess: () => { setErr(null); setStatus(""); setNote(""); invalidate(); },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : "update failed"),
  });
  const review = useMutation({
    mutationFn: (concern_resolved: boolean) => dashApi.reviewAction(action.id, { concern_resolved, note: note || undefined }),
    onSuccess: () => { setNote(""); invalidate(); },
  });

  const targets = TRANSITIONS[action.status] ?? [];
  const inp = "rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-200";
  return (
    <div className="space-y-3 border-t border-slate-800 px-3 py-3 text-xs">
      {action.detail && <p className="text-slate-400">{action.detail}</p>}
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
        <Field k="Issue" v={action.issue_statement} />
        <Field k="Planned action" v={action.planned_action} />
        <Field k="Success criteria" v={action.success_criteria} />
        <Field k="Company" v={action.company} />
        <Field k="Branch" v={action.branch} />
        <Field k="Backup owner" v={action.backup_owner} />
        <Field k="Next review" v={action.next_review_date} />
        <Field k="Completed" v={action.completed_date} />
        <Field k="Blocker" v={action.blocker_reason} />
        <Field k="Decision needed" v={action.decision_required} />
        <Field k="Completion evidence" v={action.completion_evidence} />
      </dl>

      {/* Lifecycle controls */}
      <div className="flex flex-wrap items-end gap-2 rounded border border-slate-800 bg-slate-900/40 p-2">
        <label className="flex flex-col gap-0.5">
          <span className="text-slate-500">Status →</span>
          <select className={inp} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">(keep {action.status})</option>
            {targets.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-slate-500">Owner</span>
          <input className={inp} value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="name/team" />
        </label>
        {(status === "blocked" || status === "waiting_on") && (
          <label className="flex flex-1 flex-col gap-0.5">
            <span className="text-amber-400">Blocker reason (required)</span>
            <input className={inp} value={blocker} onChange={(e) => setBlocker(e.target.value)} />
          </label>
        )}
        {status === "needs_decision" && (
          <label className="flex flex-1 flex-col gap-0.5">
            <span className="text-amber-400">Decision required (required)</span>
            <input className={inp} value={decision} onChange={(e) => setDecision(e.target.value)} />
          </label>
        )}
        {status === "resolved" && (
          <label className="flex flex-1 flex-col gap-0.5">
            <span className="text-emerald-400">Completion evidence</span>
            <input className={inp} value={evidence} onChange={(e) => setEvidence(e.target.value)} />
          </label>
        )}
        {status === "dismissed" && (
          <label className="flex flex-1 flex-col gap-0.5">
            <span className="text-amber-400">Dismissal reason (required)</span>
            <input className={inp} value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
        )}
        <button
          onClick={() => mut.mutate({
            status: status || undefined,
            owner: owner !== (action.owner ?? "") ? owner : undefined,
            blocker_reason: blocker || undefined,
            decision_required: decision || undefined,
            completion_evidence: evidence || undefined,
            note: note || undefined,
          })}
          disabled={mut.isPending}
          className="rounded bg-sky-500/20 px-3 py-1 text-sky-200 disabled:opacity-40"
        >Apply</button>
      </div>
      {err && <p className="text-rose-300">{err}</p>}

      {/* Review — confirm the original concern (never auto-closes a linked item) */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-slate-500">Confirm original concern:</span>
        <button onClick={() => review.mutate(true)} disabled={review.isPending} className="rounded bg-emerald-500/15 px-2 py-0.5 text-emerald-300 disabled:opacity-40">resolved</button>
        <button onClick={() => review.mutate(false)} disabled={review.isPending} className="rounded bg-amber-500/15 px-2 py-0.5 text-amber-300 disabled:opacity-40">not yet</button>
        <span className="text-[11px] text-slate-600">Linked acceptance/feedback items are never auto-closed.</span>
      </div>

      <LinksAndEvents links={detail.data?.links ?? []} events={detail.data?.events ?? []} />
    </div>
  );
}

function Field({ k, v }: { k: string; v: string | null }) {
  if (!v) return null;
  return <div><dt className="text-slate-500">{k}</dt><dd className="text-slate-300">{v}</dd></div>;
}

function LinksAndEvents({ links, events }: { links: ActionLink[]; events: ActionEvent[] }) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div>
        <p className="mb-1 text-slate-500">Linked records</p>
        {links.length === 0 ? <p className="text-slate-600">No links.</p> : (
          <ul className="space-y-0.5">{links.map((l) => <li key={l.id} className="text-slate-400"><span className="text-slate-300">{l.link_type}</span>: {l.label || l.link_ref}</li>)}</ul>
        )}
      </div>
      <div>
        <p className="mb-1 text-slate-500">History</p>
        {events.length === 0 ? <p className="text-slate-600">No events.</p> : (
          <ul className="max-h-40 space-y-0.5 overflow-y-auto">
            {events.map((e) => (
              <li key={e.id} className="text-slate-400">
                <span className="text-slate-500">{e.ts?.slice(0, 16).replace("T", " ")}</span>{" "}
                <span className="text-slate-300">{e.event_type}</span>
                {e.from_status && ` ${e.from_status}→${e.to_status}`}
                {e.note && <span className="text-slate-500"> — {e.note}</span>}
                {e.actor_email && <span className="text-slate-600"> ({e.actor_email})</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
